import { atom } from 'recoil';

import { iaRa } from 'lib/helpers/utility';
import { sessionPersistEffect } from 'lib/helpers/sessionPersistedAtom';
import { BATCH_ELIGIBILITY_PROGRESS_STORAGE_KEY } from 'lib/constants/sessionStorageKeys';

// Bounded: a dismissed id only has to outlive its own run's remaining ticks.
const MAX_DISMISSED = 50;

// Keep only serializable per-run fields. batchRunId equals the server run `id`, so
// the card can reconcile each restored run against the server on mount.
const sanitize = (value) => {
  const runs = value?.runs || {};
  const trimmed = {};

  Object.keys(runs).forEach((id) => {
    const run = runs[id];
    if (!run || !run.batchRunId) return;

    trimmed[id] = {
      batchRunId: run.batchRunId,
      status: run.status,
      total: run.total ?? 0,
      completed: run.completed ?? 0,
      successfulProfiles: run.successfulProfiles ?? 0,
      failedProfiles: run.failedProfiles ?? 0
    };
  });

  return {
    runs: trimmed,
    dismissed: iaRa(value?.dismissed)
      .filter((id) => id != null)
      .slice(-MAX_DISMISSED)
  };
};

// Live batch-eligibility progress, keyed by batchRunId. Written by the
// `eligibility-batch-progress` socket listener, read by the dock EligibilityDockCard,
// cleared on dismiss. Shape: { runs: { [batchRunId]: run } } where run is
// { batchRunId, status, total, completed, successfulProfiles, failedProfiles }.
//
// Persisted to sessionStorage (scoped to the active practice) so a reload rebuilds the
// card immediately. The stored status is just the last tick — EligibilityDockCard
// reconciles it against the server on mount so a run finished while away isn't stale.
export const batchEligibilityProgressState = atom({
  key: 'batchEligibilityProgressState',
  default: { runs: {}, dismissed: [] },
  effects: [sessionPersistEffect({ storageKey: BATCH_ELIGIBILITY_PROGRESS_STORAGE_KEY, sanitize })]
});
